/**
 * Service API pour BTPal
 * Gestion des appels HTTP vers le backend tenant et control plane
 */

import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { config } from '@/config';
import type {
    ApiResponse,
    PaginatedResponse,
    LoginResponse,
    User,
    UserRole,
    TeamMember,
    Invitation,
    Invoice,
    InvoiceFormData,
    Expense,
    ExpenseFormData,
    ForecastData,
    DailyForecast,
    DailyMovements,
    CashBalance,
    SimulationInput,
    SimulationResult,
    SavedSimulation,
    MultiSimulationResult,
    ApplySimulationResponse,
    Settings,
    RegisterData,
    RegisterResponse as _RegisterResponse,
    CustomerClient,
    CustomerClientFormData,
    Quote,
    QuoteFormData,
    Project,
    ProjectFormData,
    ProjectFinancialSummary,
    CatalogItem,
    CatalogItemFormData,
    Subcontractor,
    SubcontractorFormData,
    Reminder,
    OverdueInvoice,
} from '@/types';

// ========================================
// Token Management
// ========================================

const TOKEN_KEY = 'btpal_token';
const REFRESH_TOKEN_KEY = 'btpal_refresh_token';

export function getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
}

export function getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// ========================================
// Axios Instances
// ========================================

/**
 * Client API — backend BTPal
 */
export const api: AxiosInstance = axios.create({
    baseURL: `${config.apiUrl}/v1`,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000,
});

// ========================================
// Request Interceptor - Add JWT Token
// ========================================

api.interceptors.request.use(
    (cfg: InternalAxiosRequestConfig) => {
        const token = getToken();
        if (token && cfg.headers) {
            cfg.headers.Authorization = `Bearer ${token}`;
        }

        // Debug log en développement
        if (import.meta.env.DEV) {
            console.log(`[API] ${cfg.method?.toUpperCase()} ${cfg.url}`, cfg.data || '');
        }

        return cfg;
    },
    (error) => Promise.reject(error)
);

// ========================================
// Response Interceptor - Handle 401 & Refresh
// ========================================

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null): void => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Routes qui ne doivent PAS déclencher de refresh
const noRefreshRoutes = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/2fa/validate'];

api.interceptors.response.use(
    (response) => {
        // Debug log en développement
        if (import.meta.env.DEV) {
            console.log(`[API] Response:`, response.data);
        }
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as typeof error.config & { _retry?: boolean };

        // Debug log en développement
        if (import.meta.env.DEV) {
            console.error(`[API] Error:`, error.response?.status, error.response?.data || error.message);
        }

        // Ne pas tenter de refresh pour certaines routes ou si déjà retried
        const isNoRefreshRoute = originalRequest?.url && noRefreshRoutes.some(r => originalRequest.url?.includes(r));

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isNoRefreshRoute) {
            const refreshToken = getRefreshToken();

            if (!refreshToken) {
                console.log('[API] Pas de refresh token, redirection vers login');
                clearTokens();
                window.location.href = '/login';
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // Attendre que le refresh soit terminé
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return api(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                console.log('[API] Tentative de refresh token...');
                const { data } = await api.post<ApiResponse<{ token: string; refreshToken?: string }>>('/auth/refresh', {
                    refreshToken,
                });

                const newToken = data.data.token;
                setToken(newToken);
                // Store rotated refresh token (server issues a new one on each use)
                if (data.data.refreshToken) setRefreshToken(data.data.refreshToken);
                console.log('[API] Token rafraîchi avec succès');

                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }

                processQueue(null, newToken);
                return api(originalRequest);
            } catch (refreshError) {
                console.error('[API] Échec du refresh token, redirection vers login');
                processQueue(refreshError as Error, null);
                clearTokens();
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Gestion abonnement expiré / bloqué
        if (error.response?.status === 402) {
            const errorCode = (error.response.data as { error?: string })?.error;
            window.dispatchEvent(new CustomEvent('subscription:blocked', {
                detail: { code: errorCode }
            }));
            return Promise.reject(error);
        }

        // Email non vérifié
        if (error.response?.status === 403) {
            const errorCode = (error.response.data as { error?: string })?.error;
            if (errorCode === 'EMAIL_NOT_VERIFIED') {
                window.dispatchEvent(new CustomEvent('auth:email-not-verified'));
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    }
);

// ========================================
// Auth API
// ========================================

export const authApi = {
    login: async (email: string, password: string): Promise<LoginResponse> => {
        const { data } = await api.post('/auth/login', {
            email,
            password,
        });
        return normalizeApiResponse<LoginResponse>(data);
    },

    logout: async (): Promise<void> => {
        try {
            await api.post('/auth/logout');
        } finally {
            clearTokens();
        }
    },

    getMe: async (): Promise<User> => {
        const { data } = await api.get('/auth/me');
        return normalizeApiResponse<User>(data);
    },

    changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
        await api.patch('/auth/password', {
            currentPassword,
            newPassword,
            confirmPassword: newPassword,
        });
    },

    forgotPassword: async (email: string): Promise<void> => {
        await api.post('/auth/forgot-password', { email });
    },

    resetPassword: async (token: string, password: string): Promise<void> => {
        await api.post('/auth/reset-password', { token, password });
    },

    refresh: async (refreshToken: string): Promise<string> => {
        const { data } = await api.post('/auth/refresh', {
            refreshToken,
        });
        const result = normalizeApiResponse<{ token: string }>(data);
        return result.token;
    },

    // 2FA endpoints
    setup2FA: async (): Promise<{ secret: string; qrCode: string; otpauthUrl: string }> => {
        const { data } = await api.post('/auth/2fa/setup');
        return normalizeApiResponse(data);
    },

    verify2FA: async (code: string): Promise<{ backupCodes: string[] }> => {
        const { data } = await api.post('/auth/2fa/verify', { code });
        return normalizeApiResponse(data);
    },

    validate2FA: async (pendingToken: string, code: string): Promise<LoginResponse> => {
        const { data } = await api.post('/auth/2fa/validate', { pendingToken, code });
        return normalizeApiResponse<LoginResponse>(data);
    },

    disable2FA: async (password: string): Promise<void> => {
        await api.post('/auth/2fa/disable', { password });
    },

    regenerateBackupCodes: async (password: string): Promise<{ backupCodes: string[] }> => {
        const { data } = await api.post('/auth/2fa/backup-codes', { password });
        return normalizeApiResponse(data);
    },

    get2FAStatus: async (): Promise<{ enabled: boolean }> => {
        const { data } = await api.get('/auth/2fa/status');
        return normalizeApiResponse(data);
    },
};

// ========================================
// Invoices API
// ========================================

export interface InvoicesQueryParams {
    page?: number;
    pageSize?: number;
    status?: string;
    sort?: string;
    order?: 'asc' | 'desc';
    search?: string;
    projectId?: string;
}

// Helper pour normaliser une réponse API simple
// Gère les formats: { data: T }, { success: true, data: T } ou directement T
function normalizeApiResponse<T>(response: unknown): T {
    if (response === null || response === undefined) {
        return response as T;
    }

    // Si c'est un objet avec une propriété 'data', extraire data
    if (typeof response === 'object' && 'data' in response) {
        return (response as { data: T }).data;
    }

    // Sinon, c'est directement les données
    return response as T;
}

// Type pour la réponse backend (format NestJS pagination)
interface BackendPaginatedResponse<T> {
    items?: T[];
    data?: T[] | { data?: T[]; pagination?: BackendPagination };
    meta?: {
        currentPage?: number;
        totalPages?: number;
        totalItems?: number;
        itemsPerPage?: number;
    };
    pagination?: BackendPagination;
}

interface BackendPagination {
    page?: number;
    pageSize?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
}

// Helper pour normaliser la réponse paginée du backend
function normalizePaginatedResponse<T>(response: BackendPaginatedResponse<T>): PaginatedResponse<T> {
    // Format backend: { data: { data: [...], pagination: {...} } }
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const nested = response.data as { data?: T[]; pagination?: BackendPagination };
        if (nested.data && nested.pagination) {
            return {
                success: true,
                data: nested.data,
                pagination: {
                    page: nested.pagination.page || 1,
                    limit: nested.pagination.pageSize || nested.pagination.limit || 20,
                    total: nested.pagination.total || 0,
                    totalPages: nested.pagination.totalPages || 1,
                },
            };
        }
    }

    // Format: { data: [...], pagination: {...} }
    if (Array.isArray(response.data) && response.pagination) {
        return {
            success: true,
            data: response.data,
            pagination: {
                page: response.pagination.page || 1,
                limit: response.pagination.pageSize || response.pagination.limit || 20,
                total: response.pagination.total || 0,
                totalPages: response.pagination.totalPages || 1,
            },
        };
    }

    // Format NestJS: { items: [...], meta: {...} }
    const items = response.items || (Array.isArray(response.data) ? response.data : []);
    const meta = response.meta || {};

    return {
        success: true,
        data: Array.isArray(items) ? items : [],
        pagination: {
            page: meta.currentPage || 1,
            limit: meta.itemsPerPage || 20,
            total: meta.totalItems || 0,
            totalPages: meta.totalPages || 1,
        },
    };
}

export const invoicesApi = {
    getAll: async (params?: InvoicesQueryParams): Promise<PaginatedResponse<Invoice>> => {
        const { data } = await api.get<BackendPaginatedResponse<Invoice>>('/invoices', { params });
        return normalizePaginatedResponse(data);
    },

    getById: async (id: string): Promise<Invoice> => {
        const { data } = await api.get(`/invoices/${id}`);
        return normalizeApiResponse<Invoice>(data);
    },

    create: async (invoice: InvoiceFormData): Promise<Invoice> => {
        const { data } = await api.post('/invoices', invoice);
        return normalizeApiResponse<Invoice>(data);
    },

    update: async (id: string, invoice: Partial<InvoiceFormData>): Promise<Invoice> => {
        const { data } = await api.patch(`/invoices/${id}`, invoice);
        return normalizeApiResponse<Invoice>(data);
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/invoices/${id}`);
    },

    markAsPaid: async (id: string, paidDate: string): Promise<Invoice> => {
        const { data } = await api.post(`/invoices/${id}/mark-paid`, { paidDate });
        return normalizeApiResponse<Invoice>(data);
    },

    emit: async (id: string): Promise<Invoice> => {
        const { data } = await api.post(`/invoices/${id}/emit`);
        return normalizeApiResponse<Invoice>(data);
    },

    cancel: async (id: string, reason?: string): Promise<Invoice> => {
        const { data } = await api.post(`/invoices/${id}/cancel`, { reason });
        return normalizeApiResponse<Invoice>(data);
    },

    downloadPdf: async (id: string): Promise<void> => {
        const response = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
        const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `facture_${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    },

    sendByEmail: (id: string, data: { recipientEmail: string; message?: string }) =>
        api.post(`/invoices/${id}/send-email`, data),

    createCreditNote: (id: string, data: { reason?: string }) =>
        api.post(`/invoices/${id}/credit-note`, data),

    exportCompta: (params?: { startDate?: string; endDate?: string }) => {
        const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString() : '';
        return api.get(`/invoices/export/compta${qs}`, { responseType: 'text' });
    },
};

// ========================================
// Expenses API
// ========================================

export interface ExpensesQueryParams {
    page?: number;
    pageSize?: number;
    category?: string;
    status?: string;
    projectId?: string;
    recurrent?: boolean;
}

export const expensesApi = {
    getAll: async (params?: ExpensesQueryParams): Promise<PaginatedResponse<Expense>> => {
        const { data } = await api.get<BackendPaginatedResponse<Expense>>('/expenses', { params });
        return normalizePaginatedResponse(data);
    },

    getById: async (id: string): Promise<Expense> => {
        const { data } = await api.get(`/expenses/${id}`);
        return normalizeApiResponse<Expense>(data);
    },

    create: async (expense: ExpenseFormData): Promise<Expense> => {
        const { data } = await api.post('/expenses', expense);
        return normalizeApiResponse<Expense>(data);
    },

    update: async (id: string, expense: Partial<ExpenseFormData>): Promise<Expense> => {
        const { data } = await api.patch(`/expenses/${id}`, expense);
        return normalizeApiResponse<Expense>(data);
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/expenses/${id}`);
    },

    changeStatus: async (id: string, status: string): Promise<Expense> => {
        const { data } = await api.patch(`/expenses/${id}/status`, { status });
        return normalizeApiResponse<Expense>(data);
    },

    uploadAttachment: async (id: string, file: File): Promise<Expense> => {
        const form = new FormData();
        form.append('file', file);
        const { data } = await api.post(`/expenses/${id}/attachment`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return normalizeApiResponse<Expense>(data);
    },

    deleteAttachment: async (id: string): Promise<Expense> => {
        const { data } = await api.delete(`/expenses/${id}/attachment`);
        return normalizeApiResponse<Expense>(data);
    },
};

// ========================================
// Forecast API
// ========================================

export const forecastApi = {
    get: async (days: number = 90): Promise<ForecastData> => {
        const { data } = await api.get('/forecast', {
            params: { days },
        });
        const result = normalizeApiResponse<ForecastData & { data?: DailyForecast[] }>(data);

        // S'assurer que les tableaux et champs existent avec les bons noms
        return {
            currentBalance: result?.currentBalance ?? 0,
            lowestPoint: result?.lowestPoint ?? {
                date: new Date().toISOString(),
                balance: 0,
                daysFromNow: 0,
            },
            pendingInvoices: result?.pendingInvoices ?? { count: 0, total: 0 },
            totalExpenses90d: result?.totalExpenses90d ?? 0,
            overdueInvoices: result?.overdueInvoices ?? 0,
            criticalThreshold: result?.criticalThreshold ?? 5000,
            dailyForecast: result?.dailyForecast ?? result?.data ?? [],
            alerts: result?.alerts ?? [],
        };
    },

    getHistory: async (limit: number = 30): Promise<CashBalance[]> => {
        const { data } = await api.get('/forecast/history', {
            params: { limit },
        });
        return normalizeApiResponse<CashBalance[]>(data) ?? [];
    },
};

// ========================================
// Movements API (Mouvements financiers)
// ========================================

// ========================================
// Retention API
// ========================================

export interface PendingRetention {
    id: string;
    numero: string | null;
    clientName: string;
    retentionAmount: number;
    retentionDate: string;
    paidDate?: string;
    emissionDate: string;
    projectName: string | null;
}

export interface ReleasedRetention {
    id: string;
    numero: string | null;
    clientName: string;
    retentionAmount: number;
    retentionDate: string;
    emissionDate: string;
    projectName: string | null;
}

export const retentionApi = {
    /**
     * Liste les retenues de garantie en attente
     */
    getPending: async (): Promise<PendingRetention[]> => {
        const { data } = await api.get('/retention/pending');
        return normalizeApiResponse<PendingRetention[]>(data) ?? [];
    },

    /**
     * Historique des retenues déjà libérées
     */
    getReleased: async (): Promise<ReleasedRetention[]> => {
        const { data } = await api.get('/retention/released');
        return normalizeApiResponse<ReleasedRetention[]>(data) ?? [];
    },

    /**
     * Libère une retenue de garantie
     */
    release: async (invoiceId: string): Promise<Invoice> => {
        const { data } = await api.post(`/retention/${invoiceId}/release`);
        return normalizeApiResponse<Invoice>(data);
    },
};

// ========================================
// Movements API
// ========================================

export interface MovementsQueryParams {
    date?: string;
    startDate?: string;
    endDate?: string;
}

export const movementsApi = {
    /**
     * Récupère les mouvements financiers pour une date ou une plage
     */
    get: async (params: MovementsQueryParams): Promise<DailyMovements[]> => {
        const { data } = await api.get('/forecast/movements', { params });
        return normalizeApiResponse<DailyMovements[]>(data) ?? [];
    },

    /**
     * Récupère les mouvements pour une date unique
     */
    getByDate: async (date: string): Promise<DailyMovements | null> => {
        const { data } = await api.get('/forecast/movements', {
            params: { date },
        });
        const result = normalizeApiResponse<DailyMovements[]>(data);
        return result?.[0] ?? null;
    },
};

// ========================================
// Cash Balance API
// ========================================

export const cashApi = {
    // Note: GET /cash/balance n'existe pas dans l'API backend
    // Le solde actuel est dans forecast.currentBalance

    setBalance: async (balance: number, date: string): Promise<CashBalance> => {
        const { data } = await api.post('/cash/balance', {
            balance,
            date,
        });
        return normalizeApiResponse<CashBalance>(data);
    },
};

// ========================================
// Simulation API (legacy - preview only)
// ========================================

export const simulationApi = {
    simulate: async (input: SimulationInput): Promise<SimulationResult> => {
        const { data } = await api.post('/simulations/preview', input);
        return normalizeApiResponse<SimulationResult>(data);
    },
};

// ========================================
// Saved Simulations API
// ========================================

export const savedSimulationsApi = {
    getAll: async (): Promise<SavedSimulation[]> => {
        const { data } = await api.get('/simulations');
        return normalizeApiResponse<SavedSimulation[]>(data) ?? [];
    },

    getById: async (id: string): Promise<SavedSimulation> => {
        const { data } = await api.get(`/simulations/${id}`);
        return normalizeApiResponse<SavedSimulation>(data);
    },

    simulateAndSave: async (input: SimulationInput): Promise<SavedSimulation & { result: SimulationResult }> => {
        const { data } = await api.post('/simulations', input);
        return normalizeApiResponse<SavedSimulation & { result: SimulationResult }>(data);
    },

    preview: async (input: SimulationInput): Promise<SimulationResult> => {
        const { data } = await api.post('/simulations/preview', input);
        return normalizeApiResponse<SimulationResult>(data);
    },

    multi: async (simulations: SimulationInput[]): Promise<MultiSimulationResult> => {
        const { data } = await api.post('/simulations/multi', { simulations });
        return normalizeApiResponse<MultiSimulationResult>(data);
    },

    rerun: async (id: string): Promise<SavedSimulation & { result: SimulationResult }> => {
        const { data } = await api.post(`/simulations/${id}/rerun`);
        return normalizeApiResponse<SavedSimulation & { result: SimulationResult }>(data);
    },

    duplicate: async (id: string): Promise<SavedSimulation> => {
        const { data } = await api.post(`/simulations/${id}/duplicate`);
        return normalizeApiResponse<SavedSimulation>(data);
    },

    apply: async (id: string): Promise<ApplySimulationResponse> => {
        const { data } = await api.post(`/simulations/${id}/apply`);
        return normalizeApiResponse<ApplySimulationResponse>(data);
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/simulations/${id}`);
    },
};

// ========================================
// Customer Clients API
// ========================================

export interface ClientsQueryParams {
    page?: number;
    pageSize?: number;
    search?: string;
}

export const clientsApi = {
    getAll: async (params?: ClientsQueryParams): Promise<PaginatedResponse<CustomerClient>> => {
        const { data } = await api.get<BackendPaginatedResponse<CustomerClient>>('/clients', { params });
        return normalizePaginatedResponse(data);
    },

    getAllSimple: async (): Promise<CustomerClient[]> => {
        const { data } = await api.get('/clients/all');
        return normalizeApiResponse<CustomerClient[]>(data) ?? [];
    },

    getById: async (id: string): Promise<CustomerClient> => {
        const { data } = await api.get(`/clients/${id}`);
        return normalizeApiResponse<CustomerClient>(data);
    },

    create: async (client: CustomerClientFormData): Promise<CustomerClient> => {
        const { data } = await api.post('/clients', client);
        return normalizeApiResponse<CustomerClient>(data);
    },

    update: async (id: string, client: Partial<CustomerClientFormData>): Promise<CustomerClient> => {
        const { data } = await api.patch(`/clients/${id}`, client);
        return normalizeApiResponse<CustomerClient>(data);
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/clients/${id}`);
    },
};

// ========================================
// Quotes API
// ========================================

export interface QuotesQueryParams {
    page?: number;
    pageSize?: number;
    status?: string;
    sort?: string;
    order?: 'asc' | 'desc';
}

export const quotesApi = {
    getAll: async (params?: QuotesQueryParams): Promise<PaginatedResponse<Quote>> => {
        const { data } = await api.get<BackendPaginatedResponse<Quote>>('/quotes', { params });
        return normalizePaginatedResponse(data);
    },

    getById: async (id: string): Promise<Quote> => {
        const { data } = await api.get(`/quotes/${id}`);
        return normalizeApiResponse<Quote>(data);
    },

    create: async (quote: QuoteFormData): Promise<Quote> => {
        const { data } = await api.post('/quotes', quote);
        return normalizeApiResponse<Quote>(data);
    },

    update: async (id: string, quote: Partial<QuoteFormData>): Promise<Quote> => {
        const { data } = await api.patch(`/quotes/${id}`, quote);
        return normalizeApiResponse<Quote>(data);
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/quotes/${id}`);
    },

    send: async (id: string): Promise<Quote> => {
        const { data } = await api.post(`/quotes/${id}/send`);
        return normalizeApiResponse<Quote>(data);
    },

    accept: async (id: string): Promise<Quote> => {
        const { data } = await api.post(`/quotes/${id}/accept`);
        return normalizeApiResponse<Quote>(data);
    },

    refuse: async (id: string): Promise<Quote> => {
        const { data } = await api.post(`/quotes/${id}/refuse`);
        return normalizeApiResponse<Quote>(data);
    },

    convert: async (id: string): Promise<{ quote: Quote; invoice: Invoice }> => {
        const { data } = await api.post(`/quotes/${id}/convert`);
        return normalizeApiResponse<{ quote: Quote; invoice: Invoice }>(data);
    },

    duplicate: async (id: string): Promise<Quote> => {
        const { data } = await api.post(`/quotes/${id}/duplicate`);
        return normalizeApiResponse<Quote>(data);
    },

    sendByEmail: (id: string, data: { recipientEmail: string; message?: string }) =>
        api.post(`/quotes/${id}/send-email`, data),

    getAcceptanceLink: (id: string) =>
        api.get(`/quotes/${id}/acceptance-link`),

    getPublic: (token: string) =>
        api.get(`/quotes/public/${token}`),

    acceptPublic: (token: string, data: { clientName?: string }) =>
        api.post(`/quotes/public/${token}/accept`, data),

    downloadPdf: async (id: string): Promise<void> => {
        const response = await api.get(`/quotes/${id}/pdf`, { responseType: 'blob' });
        const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `devis_${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    },
};

// ========================================
// Projects API
// ========================================

export interface ProjectsQueryParams {
    page?: number;
    pageSize?: number;
    status?: string;
    search?: string;
}

export interface SimpleProject {
    id: string;
    name: string;
    status: string;
    clientName: string;
}

export const projectsApi = {
    getAll: async (params?: ProjectsQueryParams): Promise<PaginatedResponse<Project>> => {
        const { data } = await api.get<BackendPaginatedResponse<Project>>('/projects', { params });
        return normalizePaginatedResponse(data);
    },

    getAllSimple: async (): Promise<SimpleProject[]> => {
        const { data } = await api.get('/projects/all');
        return normalizeApiResponse<SimpleProject[]>(data);
    },

    getById: async (id: string): Promise<Project> => {
        const { data } = await api.get(`/projects/${id}`);
        return normalizeApiResponse<Project>(data);
    },

    getFinancialSummary: async (id: string): Promise<ProjectFinancialSummary> => {
        const { data } = await api.get(`/projects/${id}/financial-summary`);
        return normalizeApiResponse<ProjectFinancialSummary>(data);
    },

    create: async (project: ProjectFormData): Promise<Project> => {
        const { data } = await api.post('/projects', project);
        return normalizeApiResponse<Project>(data);
    },

    update: async (id: string, project: Partial<ProjectFormData & { status: string }>): Promise<Project> => {
        const { data } = await api.patch(`/projects/${id}`, project);
        return normalizeApiResponse<Project>(data);
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/projects/${id}`);
    },
};

// ========================================
// Settings API
// ========================================

export const settingsApi = {
    get: async (): Promise<Settings> => {
        const { data } = await api.get('/settings');
        return normalizeApiResponse<Settings>(data);
    },

    update: async (settings: Partial<Settings>): Promise<Settings> => {
        const { data } = await api.patch('/settings', settings);
        return normalizeApiResponse<Settings>(data);
    },

    completeOnboarding: async (): Promise<void> => {
        await api.post('/settings/complete-onboarding');
    },

    uploadLogo: async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append('logo', file);
        const { data } = await api.post('/settings/logo', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        const result = normalizeApiResponse<{ logoUrl: string }>(data);
        return result.logoUrl;
    },

    exportData: async (): Promise<Blob> => {
        const { data } = await api.post('/settings/export', null, {
            responseType: 'blob',
        });
        return data;
    },

    deleteAccount: async (): Promise<void> => {
        await api.delete('/settings/account');
    },
};

// ========================================
// Control Plane API (Inscription)
// ========================================

// ========================================
// Catalog API
// ========================================

export const catalogApi = {
    getAll: async (params?: { search?: string; category?: string }): Promise<CatalogItem[]> => {
        const { data } = await api.get('/catalog', { params });
        return normalizeApiResponse<CatalogItem[]>(data) ?? [];
    },

    getCategories: async (): Promise<string[]> => {
        const { data } = await api.get('/catalog/categories');
        return normalizeApiResponse<string[]>(data) ?? [];
    },

    create: async (item: CatalogItemFormData): Promise<CatalogItem> => {
        const { data } = await api.post('/catalog', item);
        return normalizeApiResponse<CatalogItem>(data);
    },

    update: async (id: string, item: Partial<CatalogItemFormData>): Promise<CatalogItem> => {
        const { data } = await api.put(`/catalog/${id}`, item);
        return normalizeApiResponse<CatalogItem>(data);
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/catalog/${id}`);
    },
};

// ========================================
// Subcontractors API
// ========================================

export const subcontractorsApi = {
    getAll: async (): Promise<Subcontractor[]> => {
        const { data } = await api.get('/subcontractors');
        return normalizeApiResponse<Subcontractor[]>(data) ?? [];
    },

    create: async (item: SubcontractorFormData): Promise<Subcontractor> => {
        const { data } = await api.post('/subcontractors', item);
        return normalizeApiResponse<Subcontractor>(data);
    },

    update: async (id: string, item: Partial<SubcontractorFormData>): Promise<Subcontractor> => {
        const { data } = await api.put(`/subcontractors/${id}`, item);
        return normalizeApiResponse<Subcontractor>(data);
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/subcontractors/${id}`);
    },
};

// ========================================
// Reminders API
// ========================================

export const remindersApi = {
    getOverdue: async (): Promise<OverdueInvoice[]> => {
        const { data } = await api.get('/reminders');
        return normalizeApiResponse<OverdueInvoice[]>(data) ?? [];
    },

    getHistory: async (): Promise<Reminder[]> => {
        const { data } = await api.get('/reminders/history');
        return normalizeApiResponse<Reminder[]>(data) ?? [];
    },

    getByInvoice: async (invoiceId: string): Promise<Reminder[]> => {
        const { data } = await api.get(`/reminders/invoice/${invoiceId}`);
        return normalizeApiResponse<Reminder[]>(data) ?? [];
    },

    create: async (reminder: { invoiceId: string; type: string; notes?: string }): Promise<Reminder> => {
        const { data } = await api.post('/reminders', reminder);
        return normalizeApiResponse<Reminder>(data);
    },
};

// ========================================
// Export API
// ========================================

export const exportApi = {
    downloadFEC: async (startDate: string, endDate: string): Promise<Blob> => {
        const { data } = await api.get('/export/fec', {
            params: { startDate, endDate },
            responseType: 'blob',
        });
        return data;
    },
};

// ========================================
// Situations API
// ========================================

export const situationsApi = {
    getByQuote: async (quoteId: string): Promise<Invoice[]> => {
        const { data } = await api.get(`/situations/quote/${quoteId}`);
        return normalizeApiResponse<Invoice[]>(data);
    },
    getProgress: async (quoteId: string): Promise<Record<number, number>> => {
        const { data } = await api.get(`/situations/progress/${quoteId}`);
        return normalizeApiResponse<Record<number, number>>(data);
    },
    create: async (quoteId: string, lines: Array<{ lineIndex: number; currentPercent: number }>): Promise<Invoice> => {
        const { data } = await api.post('/situations', { quoteId, lines });
        return normalizeApiResponse<Invoice>(data);
    },
};

// ========================================
// Team API
// ========================================

export const teamApi = {
    getMembers: async (): Promise<TeamMember[]> => {
        const { data } = await api.get('/team/members');
        return normalizeApiResponse<TeamMember[]>(data);
    },
    getInvitations: async (): Promise<Invitation[]> => {
        const { data } = await api.get('/team/invitations');
        return normalizeApiResponse<Invitation[]>(data);
    },
    invite: async (email: string, role: UserRole): Promise<Invitation> => {
        const { data } = await api.post('/team/invite', { email, role });
        return normalizeApiResponse<Invitation>(data);
    },
    updateMemberRole: async (memberId: string, role: UserRole): Promise<TeamMember> => {
        const { data } = await api.patch(`/team/members/${memberId}/role`, { role });
        return normalizeApiResponse<TeamMember>(data);
    },
    removeMember: async (memberId: string): Promise<void> => {
        await api.delete(`/team/members/${memberId}`);
    },
    cancelInvitation: async (invitationId: string): Promise<void> => {
        await api.delete(`/team/invitations/${invitationId}`);
    },
    acceptInvitation: async (token: string, password: string, firstName?: string, lastName?: string) => {
        const { data } = await api.post('/team/accept-invitation', { token, password, firstName, lastName });
        return normalizeApiResponse(data);
    },
};

// ========================================
// Register API
// ========================================

export const registerApi = {
    register: async (registerData: RegisterData): Promise<LoginResponse> => {
        const { data } = await api.post('/auth/register', registerData);
        return normalizeApiResponse<LoginResponse>(data);
    },
};

// ========================================
// Dashboard API
// ========================================

export const dashboardApi = {
    getActionItems: () => api.get('/dashboard/actions'),
};

// ========================================
// Import API
// ========================================

export const importApi = {
    importClients: (csv: string) => api.post('/import/clients', { csv }),
    importCatalogue: (csv: string) => api.post('/import/catalogue', { csv }),
    importBank: (csv: string) => api.post('/import/bank', { csv }),
    getBankTransactions: (params?: { startDate?: string; endDate?: string }) =>
        api.get('/import/bank/transactions', { params }),
    matchTransaction: (id: string, matchedInvoiceId: string | null, matchedExpenseId: string | null) =>
        api.patch(`/import/bank/transactions/${id}/match`, { matchedInvoiceId, matchedExpenseId }),
    unmatchTransaction: (id: string) =>
        api.patch(`/import/bank/transactions/${id}/unmatch`),
};

// ========================================
// Rentabilite API
// ========================================

export const rentabiliteApi = {
    getProjects: () => api.get('/rentabilite/projects'),
    getProject: (id: string) => api.get(`/rentabilite/projects/${id}`),
};

// ========================================
// Feedback API
// ========================================

// ========================================
// Announcement API
// ========================================

export type AnnouncementType = 'info' | 'warning' | 'maintenance';

export interface Announcement {
    message: string;
    type: AnnouncementType;
    setAt: string;
}

export const announcementApi = {
    get: async (): Promise<Announcement | null> => {
        const { data } = await api.get('/announcement');
        return normalizeApiResponse<Announcement | null>(data);
    },
};

export const stripeApi = {
    createCheckoutSession: async (plan: 'monthly' | 'annual', withdrawalWaiverAccepted: boolean): Promise<{ url: string }> => {
        const { data } = await api.post('/stripe/create-checkout-session', { plan, withdrawalWaiverAccepted });
        return normalizeApiResponse<{ url: string }>(data);
    },
    createCustomerPortal: async (): Promise<{ url: string }> => {
        const { data } = await api.post('/stripe/customer-portal');
        return normalizeApiResponse<{ url: string }>(data);
    },
    syncStatus: async (): Promise<{ status: string; period?: string }> => {
        const { data } = await api.post('/stripe/sync-status');
        return normalizeApiResponse<{ status: string; period?: string }>(data);
    },
};

export const feedbackApi = {
    submitChurn: async (data: {
        email: string;
        organizationId?: string;
        reason: string;
        comment?: string;
    }): Promise<void> => {
        await api.post('/feedback/churn', data);
    },

    report: async (data: {
        type: 'bug' | 'suggestion';
        subject: string;
        steps?: string;
        expected?: string;
        actual?: string;
        description?: string;
        page?: string;
        userAgent?: string;
    }): Promise<void> => {
        await api.post('/feedback/report', data);
    },
};

// ========================================
// Plateforme Agréée (PA) — facturation électronique
// ========================================

export interface PaConnectionStatus {
    connected: boolean;
    connection: {
        provider: string;
        status: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'ERROR';
        paCompanyName: string | null;
        paCompanySiret: string | null;
        connectedAt: string | null;
        lastSyncAt: string | null;
        lastErrorAt: string | null;
        lastErrorMessage: string | null;
        tokenExpiresAt: string | null;
    } | null;
}

export const paApi = {
    /** Récupère le statut de la connexion PA pour l'org courante */
    getStatus: async (): Promise<PaConnectionStatus> => {
        const { data } = await api.get('/pa/status');
        return normalizeApiResponse<PaConnectionStatus>(data);
    },

    /**
     * Démarre le flow OAuth : retourne l'URL SuperPDP vers laquelle rediriger.
     * Le front fait ensuite window.location.href = result.authorizeUrl.
     */
    authorize: async (redirectAfter?: string): Promise<{ authorizeUrl: string }> => {
        const { data } = await api.post('/pa/authorize', { redirectAfter });
        return normalizeApiResponse<{ authorizeUrl: string }>(data);
    },

    /** Test la connexion via /companies/me */
    test: async (): Promise<{ ok: boolean; company: { name: string; siret?: string } }> => {
        const { data } = await api.post('/pa/test');
        return normalizeApiResponse<{ ok: boolean; company: { name: string; siret?: string } }>(
            data
        );
    },

    /** Révoque la connexion PA */
    disconnect: async (): Promise<void> => {
        await api.post('/pa/disconnect');
    },

    /** Réessaie l'envoi d'une facture vers la PA */
    retryInvoice: async (
        invoiceId: string
    ): Promise<{
        ok: boolean;
        skipped?: boolean;
        paInvoiceId?: string;
        errorCode?: string;
        errorMessage?: string;
    }> => {
        const { data } = await api.post(`/invoices/${invoiceId}/retry-pa`);
        return normalizeApiResponse(data);
    },
};
